import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import {
  CircleMarker,
  MapContainer,
  Popup,
  TileLayer,
  useMap,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { MapPinned, Maximize2, Minimize2, X } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import {
  getOccupancyRange,
  hasValidCoordinates,
  normalizeNumber,
  normalizeText,
} from '../utils/ocupacaoRedeAnalytics';

const DEFAULT_CENTER = [-3.5, -54.8];
const DEFAULT_ZOOM = 7;
const MARKER_CANVAS_RENDERER = L.canvas({ padding: 0.5 });
const CLUSTER_GROUP_PROPS = {
  chunkedLoading: true,
  chunkInterval: 120,
  chunkDelay: 40,
  animate: false,
  showCoverageOnHover: false,
  spiderfyOnMaxZoom: false,
  removeOutsideVisibleBounds: true,
  maxClusterRadius: 48,
};

const panelClassName =
  'rounded-[24px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-[0_16px_40px_rgba(15,23,42,0.10)]';

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const getMarkerColor = (rate) => {
  const normalizedRate = normalizeNumber(rate);

  if (normalizedRate > 80) {
    return '#ef4444';
  }

  if (normalizedRate > 50) {
    return '#f59e0b';
  }

  return '#22c55e';
};

const getMarkerRadius = (capacidade) => (normalizeNumber(capacidade) === 16 ? 6 : 4);

const MAP_LEGEND = [
  { label: 'Baixa ocupação', color: '#22c55e', hint: 'até 50%' },
  { label: 'Média ocupação', color: '#f59e0b', hint: 'de 50% a 80%' },
  { label: 'Crítica', color: '#ef4444', hint: 'acima de 80%' },
];

const buildPointsViewportSignature = (points) => {
  if (!Array.isArray(points) || points.length === 0) {
    return 'default';
  }

  if (points.length === 1) {
    const [lat, lng] = points[0];
    return `single:${Number(lat).toFixed(4)}:${Number(lng).toFixed(4)}`;
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  points.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  return [
    points.length,
    minLat.toFixed(3),
    maxLat.toFixed(3),
    minLng.toFixed(3),
    maxLng.toFixed(3),
  ].join(':');
};

const buildClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 42 : count < 100 ? 50 : 58;
  const allChildren = cluster.getAllChildMarkers();
  const averageRate = allChildren.length > 0
    ? allChildren.reduce((acc, marker) => (
      acc + normalizeNumber(marker?.options?.occupancyRate)
    ), 0) / allChildren.length
    : 0;
  const markerColor = getMarkerColor(averageRate);
  const ringColor = getOccupancyRange(averageRate) === 'Critica'
    ? 'rgba(239, 68, 68, 0.18)'
    : getOccupancyRange(averageRate) === 'Media'
      ? 'rgba(245, 158, 11, 0.18)'
      : 'rgba(34, 197, 94, 0.18)';

  return L.divIcon({
    html: `
      <div class="infra-cluster-shell" style="
        width:${size}px;
        height:${size}px;
        background:radial-gradient(circle at 30% 30%, ${markerColor} 0%, ${markerColor}dd 100%);
        box-shadow:0 10px 24px ${ringColor};
      ">
        <span>${count}</span>
      </div>
    `,
    className: 'infra-cluster-icon',
    iconSize: [size, size],
  });
};

const FitMapToBounds = ({ points }) => {
  const map = useMap();
  const previousSignatureRef = useRef(null);

  useEffect(() => {
    const nextSignature = buildPointsViewportSignature(points);

    if (previousSignatureRef.current === nextSignature) {
      return;
    }

    previousSignatureRef.current = nextSignature;

    if (!Array.isArray(points) || points.length === 0) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM, { animate: false });
      return;
    }

    if (points.length === 1) {
      map.setView(points[0], 13, { animate: false });
      return;
    }

    map.fitBounds(points, {
      padding: [32, 32],
      animate: false,
      maxZoom: 14,
    });
  }, [map, points]);

  return null;
};

const RefreshMapSize = ({ refreshToken }) => {
  const map = useMap();

  useEffect(() => {
    let rafId = 0;
    let nestedRafId = 0;

    rafId = window.requestAnimationFrame(() => {
      nestedRafId = window.requestAnimationFrame(() => {
        map.invalidateSize({ pan: false, debounceMoveend: true });
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
      window.cancelAnimationFrame(nestedRafId);
    };
  }, [map, refreshToken]);

  return null;
};

const MapViewport = ({
  markerPoints,
  markers,
  height = '500px',
  minHeight = '450px',
  fillHeight = false,
  refreshToken,
}) => (
  <div className={`overflow-hidden rounded-[22px] border border-[color:var(--border)] ${fillHeight ? 'flex min-h-0 flex-1 flex-col' : ''}`}>
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      scrollWheelZoom
      preferCanvas
      zoomAnimation={false}
      fadeAnimation={false}
      markerZoomAnimation={false}
      className={`w-full ${fillHeight ? 'h-full flex-1' : ''}`}
      style={{ height, minHeight }}
    >
      <RefreshMapSize refreshToken={refreshToken} />
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <FitMapToBounds points={markerPoints} />
      <MarkerClusterGroup {...CLUSTER_GROUP_PROPS} iconCreateFunction={buildClusterIcon}>
        {markers}
      </MarkerClusterGroup>
    </MapContainer>
  </div>
);

const NetworkMap = ({ rows = [], loading = false, isSyncing = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isExpanded || typeof document === 'undefined') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded]);

  const validRows = useMemo(
    () => rows.filter((row) => hasValidCoordinates(row)),
    [rows],
  );

  const markerPoints = useMemo(
    () => validRows.map((row) => [row.latitude, row.longitude]),
    [validRows],
  );

  const markers = useMemo(() => (
    validRows.map((cto) => (
      <CircleMarker
        key={cto.id_cto}
        center={[cto.latitude, cto.longitude]}
        radius={getMarkerRadius(cto.capacidade)}
        renderer={MARKER_CANVAS_RENDERER}
        occupancyRate={normalizeNumber(cto.percentual_ocupacao)}
        pathOptions={{
          color: getMarkerColor(cto.percentual_ocupacao),
          fillColor: getMarkerColor(cto.percentual_ocupacao),
          fillOpacity: 0.9,
          weight: 1,
        }}
      >
        <Popup>
          <div className="min-w-[220px] text-sm text-slate-800">
            <strong className="block text-base text-slate-900">{normalizeText(cto.caixa)}</strong>
            <div className="mt-2 space-y-1">
              <div>Cidade: {normalizeText(cto.cidade)}</div>
              <div>Projeto: {normalizeText(cto.projeto)}</div>
              <div>POP: {normalizeText(cto.pop)}</div>
            </div>
            <hr className="my-3 border-slate-200" />
            <div className="space-y-1">
              <div>Capacidade: {normalizeNumber(cto.capacidade)}</div>
              <div>Ocupadas: {normalizeNumber(cto.portas_ocupadas)}</div>
              <div>Vagas: {normalizeNumber(cto.portas_vagas)}</div>
              <div>Ocupação: {percentFormatter.format(normalizeNumber(cto.percentual_ocupacao))}%</div>
            </div>
          </div>
        </Popup>
      </CircleMarker>
    ))
  ), [validRows]);

  return (
    <>
      {isExpanded ? (
        <div className="fixed inset-0 z-[119] bg-slate-950/78" onClick={() => setIsExpanded(false)} aria-hidden="true" />
      ) : null}

      <div
        className={
          isExpanded
            ? 'fixed inset-x-4 top-[calc(var(--header-shell-height)+12px)] bottom-4 z-[120] flex flex-col overflow-hidden rounded-[28px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] p-4 shadow-[0_28px_80px_rgba(2,6,23,0.45)] sm:inset-x-8 sm:top-[calc(var(--header-shell-height)+16px)] sm:bottom-6 sm:p-5 lg:inset-x-12'
            : `${panelClassName} h-full`
        }
      >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className={`${isExpanded ? 'text-lg' : 'text-sm'} font-semibold text-[color:var(--text-primary)]`}>
            {isExpanded ? 'Distribuição de CTOs FTTH' : 'Mapa geográfico de CTOs'}
          </h3>
          <p className="mt-1 text-xs text-[color:var(--text-muted)]">
            {isExpanded
              ? 'Navegue com zoom, arraste o mapa e clique nos clusters ou marcadores para abrir os detalhes.'
              : 'Distribuição real da malha FTTH com clusterização e leitura por ocupação.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] px-3 py-1 text-[11px] text-[color:var(--text-muted)]">
            <MapPinned className="h-3.5 w-3.5" />
            OpenStreetMap
          </span>
          {isExpanded ? (
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
              aria-label="Fechar mapa expandido"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      <div className={`mb-3 flex items-center ${isExpanded ? 'justify-between' : 'justify-end'}`}>
        {isExpanded ? (
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--text-muted)]">
            <MapPinned className="h-3.5 w-3.5 text-[color:var(--accent)]" />
            Mapa geográfico expandido
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => setIsExpanded((current) => !current)}
          className="inline-flex min-h-[38px] items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-3 py-1.5 text-[11px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
          aria-label={isExpanded ? 'Minimizar mapa geográfico' : 'Maximizar mapa geográfico'}
        >
          {isExpanded ? (
            <>
              <Minimize2 className="h-3.5 w-3.5 text-[color:var(--accent)]" />
              Minimizar
            </>
          ) : (
            <>
              <Maximize2 className="h-3.5 w-3.5 text-[color:var(--accent)]" />
              Expandir mapa
            </>
          )}
        </button>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2.5">
        {MAP_LEGEND.map((item) => (
          <div
            key={item.label}
            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-strong)] px-3 py-1.5 text-[11px] text-[color:var(--text-muted)]"
          >
            <span
              className="inline-flex h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
            <span className="font-medium text-[color:var(--text-primary)]">{item.label}</span>
            <span className="text-[color:var(--text-muted)]/90">{item.hint}</span>
          </div>
        ))}
      </div>

      {!loading && isSyncing ? (
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[color:var(--header-control-border-subtle)] bg-[color:var(--surface-muted)]/72 px-3 py-1.5 text-[11px] text-[color:var(--text-muted)]">
          <span className="inline-flex h-2 w-2 rounded-full bg-[color:var(--accent)] animate-pulse" aria-hidden="true" />
          Mapa sincronizando com os filtros
        </div>
      ) : null}

      {loading ? (
        <div className="min-h-[450px] animate-pulse rounded-[22px] border border-[color:var(--border)] bg-[color:var(--surface-strong)]" />
      ) : null}

      {!loading && rows.length === 0 ? (
        <div className="flex min-h-[450px] items-center justify-center rounded-[22px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-6 text-center text-sm text-[color:var(--text-muted)]">
          Nenhuma CTO encontrada para os filtros selecionados.
        </div>
      ) : null}

      {!loading && rows.length > 0 && validRows.length === 0 ? (
        <div className="flex min-h-[450px] items-center justify-center rounded-[22px] border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-6 text-center text-sm text-[color:var(--text-muted)]">
          Os filtros retornaram registros, mas nenhuma CTO possui coordenadas válidas para exibição no mapa.
        </div>
      ) : null}

      {!loading && validRows.length > 0 ? (
        <div className={isExpanded ? 'flex min-h-0 flex-1 flex-col' : ''}>
          <MapViewport
            markerPoints={markerPoints}
            markers={markers}
            height={isExpanded ? '100%' : '500px'}
            minHeight={isExpanded ? '100%' : '450px'}
            fillHeight={isExpanded}
            refreshToken={isExpanded ? 'expanded' : 'compact'}
          />
        </div>
      ) : null}
      </div>
    </>
  );
};

export default memo(NetworkMap);
